/**
 * 練習の結果と書きかけのコードを、手元のブラウザ (localStorage) だけに残す。サーバーには残さない (2026-10-30 親の決め)。
 * 読み書きはすべて try/catch で包む。使えない環境 (プライベートの窓、保存の禁止) でも、頁は動いて解ける。
 */
import type { JudgeLanguage } from "@/lib/stdio-judge/types";

const PROGRESS_KEY = "coding-test-progress-v1";

export type ProblemProgress = {
  /** 直近の提出で通ったテストの数と全部の数 */
  passed: number;
  total: number;
  language: JudgeLanguage;
  /** 直近の提出の時刻 (ミリ秒) */
  at: number;
  /** 一度でも全部通したことがある */
  solved: boolean;
};

export type ProgressStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserStorage(): ProgressStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readAllProgress(storage: ProgressStorage | null = browserStorage()): Record<string, ProblemProgress> {
  if (!storage) return {};
  try {
    const parsed: unknown = JSON.parse(storage.getItem(PROGRESS_KEY) ?? "{}");
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, ProblemProgress> = {};
    for (const [slug, value] of Object.entries(parsed)) {
      const v = value as Partial<ProblemProgress> | null;
      if (
        v &&
        typeof v.passed === "number" &&
        typeof v.total === "number" &&
        typeof v.at === "number" &&
        (v.language === "python" || v.language === "javascript")
      ) {
        out[slug] = { passed: v.passed, total: v.total, language: v.language, at: v.at, solved: v.solved === true };
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** 提出のたびに、直近の結果で上書きする。一度全部通した印 (solved) は残す */
export function saveProgress(
  slug: string,
  result: { passed: number; total: number; language: JudgeLanguage; at: number },
  storage: ProgressStorage | null = browserStorage(),
): ProblemProgress | null {
  if (!storage) return null;
  try {
    const all = readAllProgress(storage);
    const next: ProblemProgress = { ...result, solved: result.total > 0 && result.passed === result.total ? true : all[slug]?.solved === true };
    all[slug] = next;
    storage.setItem(PROGRESS_KEY, JSON.stringify(all));
    return next;
  } catch {
    return null;
  }
}

export const draftKey = (slug: string, language: JudgeLanguage) => `coding-test-draft-v1:${slug}:${language}`;

export function readDraft(slug: string, language: JudgeLanguage, storage: ProgressStorage | null = browserStorage()): string | null {
  try {
    return storage?.getItem(draftKey(slug, language)) ?? null;
  } catch {
    return null;
  }
}

/** code が null なら消す */
export function writeDraft(slug: string, language: JudgeLanguage, code: string | null, storage: ProgressStorage | null = browserStorage()) {
  try {
    if (!storage) return;
    if (code === null) storage.removeItem(draftKey(slug, language));
    else storage.setItem(draftKey(slug, language), code);
  } catch {
    /* 残せなくても解ける */
  }
}
